/**
 * Shopping CrossFrame Listeners
 *
 * Handles CrossFrame requests for shopping trip operations and assisted shopping
 * (customer authentication and cart management).
 * Validates required parameters and delegates to the gateway client via getServiceClient().
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'
import { GetShoppingTripInput } from '@amzn/coral_com-amazon-ihmartax-coral/GetShoppingTripInput'
import { FindShoppingTripInput } from '@amzn/coral_com-amazon-ihmartax-coral/FindShoppingTripInput'
import { ReturnItemInput } from '@amzn/coral_com-amazon-ihmartax-coral/ReturnItemInput'
import { ShoppingTrip } from '@amzn/coral_com-amazon-ihmartax-coral/ShoppingTrip'
import { ReturnItemOutput } from '@amzn/coral_com-amazon-ihmartax-coral/ReturnItemOutput'
import { GetPersonIdByCardInfoInput } from '@amzn/coral_com-amazon-ihmfakeblockservice/GetPersonIdByCardInfoInput'
import { GetPersonIdByCardInfoOutput } from '@amzn/coral_com-amazon-ihmfakeblockservice/GetPersonIdByCardInfoOutput'
import { GetPersonIdByRecognitionTokenInput } from '@amzn/coral_com-amazon-ihmfakeblockservice/GetPersonIdByRecognitionTokenInput'
import { GetPersonIdByRecognitionTokenOutput } from '@amzn/coral_com-amazon-ihmfakeblockservice/GetPersonIdByRecognitionTokenOutput'
import { SubmitCartOperationByCardInfoInput } from '@amzn/coral_com-amazon-ihmfakeblockservice/SubmitCartOperationByCardInfoInput'
import { SubmitCartOperationByCardInfoOutput } from '@amzn/coral_com-amazon-ihmfakeblockservice/SubmitCartOperationByCardInfoOutput'
import { SubmitCartOperationByPersonIdInput } from '@amzn/coral_com-amazon-ihmfakeblockservice/SubmitCartOperationByPersonIdInput'
import { SubmitCartOperationByPersonIdOutput } from '@amzn/coral_com-amazon-ihmfakeblockservice/SubmitCartOperationByPersonIdOutput'
import { validateRequired, validateRequiredNumeric } from './listenerUtils'

export async function getShoppingTripListener (message: any): Promise<ShoppingTrip> {
    const params = (message && message.data) || {}
    validateRequired(params, 'receiptId', 'storeId')
    const client = getServiceClient()
    return client.getShoppingTrip(GetShoppingTripInput.clone(params)).toPromise()
}

export async function findShoppingTripListener (message: any): Promise<ShoppingTrip> {
    const params = (message && message.data) || {}
    validateRequired(params, 'cardInfo', 'externalItemId', 'storeId')
    const client = getServiceClient()
    return client.findShoppingTrip(FindShoppingTripInput.clone(params)).toPromise()
}

export async function returnItemListener (message: any): Promise<ReturnItemOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'receiptId', 'purchaseId', 'lineItemId', 'reasonCode', 'storeId')
    validateRequiredNumeric(params, 'from', 'to')
    const client = getServiceClient()
    return client.returnItem(ReturnItemInput.clone(params)).toPromise()
}

export async function refundItemListener (message: any): Promise<ReturnItemOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'receiptId', 'purchaseId', 'lineItemId', 'storeId')
    const client = getServiceClient()
    return client.returnItem(ReturnItemInput.clone({ ...params, returnType: 'ITEM_LEVEL_REFUND' })).toPromise()
}

export async function refundOrderListener (message: any): Promise<ReturnItemOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'receiptId', 'purchaseId', 'storeId')
    const client = getServiceClient()
    return client.returnItem(ReturnItemInput.clone({ ...params, returnType: 'ORDER_LEVEL_REFUND' })).toPromise()
}

export async function getPersonIdByRecognitionTokenListener (message: any): Promise<GetPersonIdByRecognitionTokenOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'entryKey', 'associateAppEvent')
    const client = getServiceClient()
    return client.getPersonIdByRecognitionToken(GetPersonIdByRecognitionTokenInput.clone(params)).toPromise()
}

export async function getPersonIdByCardInfoListener (message: any): Promise<GetPersonIdByCardInfoOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'paymentInfo')
    const client = getServiceClient()
    return client.getPersonIdByCardInfo(GetPersonIdByCardInfoInput.clone(params)).toPromise()
}

export async function submitCartOperationByPersonIdListener (message: any): Promise<SubmitCartOperationByPersonIdOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'asin', 'cartOperationType', 'personId', 'entryKey', 'associateAppEvent', 'quantity')
    const client = getServiceClient()
    return client.submitCartOperationByPersonId(SubmitCartOperationByPersonIdInput.clone(params)).toPromise()
}

export async function submitCartOperationByCardInfoListener (message: any): Promise<SubmitCartOperationByCardInfoOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'asin', 'cartOperationType', 'personId', 'paymentInfo', 'associateAppEvent', 'quantity')
    const client = getServiceClient()
    return client.submitCartOperationByCardInfo(SubmitCartOperationByCardInfoInput.clone(params)).toPromise()
}
